import React, { useEffect, useRef, useState, useCallback } from 'react'

import {
	ChevronDown,
	ChevronUp,
	X,
	Play,
	Pause,
	Volume2,
	VolumeX,
} from 'lucide-react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import defaultImage from '@/assets/images/default-image.png'
import { globalAudio } from '@/core/services/globalAudio'
import { RootState } from '@/core/store'
import {
	closePlayer,
	toggleCollapse,
	updatePosition,
	setIsPlaying,
} from '@/core/store/slices/globalPlayer'

const DEBUG_GLOBAL_PLAYER_AUDIO = import.meta.env.DEV

const GlobalPlayer: React.FC = () => {
	const dispatch = useDispatch()
	const { isOpen, isCollapsed, position, content, isPlaying } = useSelector(
		(state: RootState) => state.globalPlayer
	)
	const containerRef = useRef<HTMLDivElement>(null)
	const [isDragging, setIsDragging] = useState(false)
	const [playerPosition, setPlayerPosition] = useState(position)
	const [currentTime, setCurrentTime] = useState(0)
	const [duration, setDuration] = useState(0)
	const [volume, setVolume] = useState(1)
	const [isMuted, setIsMuted] = useState(false)
	const [coverSrc, setCoverSrc] = useState(defaultImage)
	const dragStartRef = useRef<{
		x: number
		y: number
		startX: number
		startY: number
	} | null>(null)

	// Set initial position
	useEffect(() => {
		if (isOpen) {
			setPlayerPosition(position)
		}
	}, [isOpen, position])

	// Update cover when content changes
	useEffect(() => {
		setCoverSrc(content?.cover || defaultImage)
	}, [content?.cover])

	// Sync with globalAudio events
	useEffect(() => {
		if (!isOpen || !content) return

		const audio = globalAudio.getAudioElement()

		const handlePlay = () => {
			if (DEBUG_GLOBAL_PLAYER_AUDIO) {
				console.debug('[global-player] audio play event')
			}
			dispatch(setIsPlaying(true))
		}

		const handlePause = () => {
			if (DEBUG_GLOBAL_PLAYER_AUDIO) {
				console.debug('[global-player] audio pause event')
			}
			dispatch(setIsPlaying(false))
		}

		const handleTimeUpdate = () => {
			setCurrentTime(audio.currentTime)
		}

		const handleLoadedMetadata = () => {
			setDuration(audio.duration)
			if (DEBUG_GLOBAL_PLAYER_AUDIO) {
				console.debug('[global-player] loadedmetadata', {
					duration: audio.duration,
				})
			}
		}

		const handleEnded = () => {
			dispatch(setIsPlaying(false))
		}

		const handleError = () => {
			if (DEBUG_GLOBAL_PLAYER_AUDIO) {
				console.debug('[global-player] audio error', audio.error)
			}
		}

		audio.addEventListener('play', handlePlay)
		audio.addEventListener('pause', handlePause)
		audio.addEventListener('timeupdate', handleTimeUpdate)
		audio.addEventListener('loadedmetadata', handleLoadedMetadata)
		audio.addEventListener('ended', handleEnded)
		audio.addEventListener('error', handleError)

		// Sync initial state
		setCurrentTime(audio.currentTime)
		setDuration(audio.duration || 0)
		dispatch(setIsPlaying(!audio.paused))

		return () => {
			audio.removeEventListener('play', handlePlay)
			audio.removeEventListener('pause', handlePause)
			audio.removeEventListener('timeupdate', handleTimeUpdate)
			audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
			audio.removeEventListener('ended', handleEnded)
			audio.removeEventListener('error', handleError)
		}
	}, [isOpen, content, dispatch])

	// Handle play/pause toggle
	const handlePlayPause = useCallback(() => {
		const audio = globalAudio.getAudioElement()
		if (audio.paused) {
			audio.play().catch((err) => {
				if (DEBUG_GLOBAL_PLAYER_AUDIO) {
					console.debug('[global-player] play failed', err)
				}
			})
		} else {
			audio.pause()
		}
	}, [])

	// Handle seek
	const handleSeek = useCallback(
		(
			e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
		) => {
			const bar = e.currentTarget
			const rect = bar.getBoundingClientRect()
			const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
			const percent = Math.max(
				0,
				Math.min(1, (clientX - rect.left) / rect.width)
			)
			const audio = globalAudio.getAudioElement()
			if (audio.duration) {
				audio.currentTime = percent * audio.duration
			}
		},
		[]
	)

	// Handle volume change
	const handleVolumeChange = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			const bar = e.currentTarget
			const rect = bar.getBoundingClientRect()
			const percent = Math.max(
				0,
				Math.min(1, (e.clientX - rect.left) / rect.width)
			)
			const audio = globalAudio.getAudioElement()
			audio.volume = percent
			setVolume(percent)
			if (percent > 0) {
				audio.muted = false
				setIsMuted(false)
			}
		},
		[]
	)

	// Handle mute toggle
	const handleMuteToggle = useCallback(() => {
		const audio = globalAudio.getAudioElement()
		audio.muted = !audio.muted
		setIsMuted(audio.muted)
	}, [])

	// Format time as mm:ss
	const formatTime = (seconds: number) => {
		if (!isFinite(seconds) || isNaN(seconds)) return '0:00'
		const mins = Math.floor(seconds / 60)
		const secs = Math.floor(seconds % 60)
		return `${mins}:${secs.toString().padStart(2, '0')}`
	}

	// Handle mouse down for drag start
	const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
		if (containerRef.current) {
			setIsDragging(true)

			dragStartRef.current = {
				x: playerPosition.x,
				y: playerPosition.y,
				startX: e.clientX,
				startY: e.clientY,
			}
		}
	}

	// Handle mouse move for dragging
	const handleMouseMove = useCallback(
		(e: MouseEvent) => {
			if (isDragging && dragStartRef.current) {
				const deltaX = e.clientX - dragStartRef.current.startX
				const deltaY = e.clientY - dragStartRef.current.startY

				setPlayerPosition({
					x: dragStartRef.current.x + deltaX,
					y: dragStartRef.current.y + deltaY,
				})
			}
		},
		[isDragging]
	)

	// Handle mouse up to end dragging
	const handleMouseUp = useCallback(() => {
		if (isDragging) {
			setIsDragging(false)
			dragStartRef.current = null

			// Save position to Redux
			dispatch(updatePosition(playerPosition))
		}
	}, [isDragging, playerPosition, dispatch])

	// Add and remove event listeners for drag
	useEffect(() => {
		if (isDragging) {
			window.addEventListener('mousemove', handleMouseMove)
			window.addEventListener('mouseup', handleMouseUp)
		}

		return () => {
			window.removeEventListener('mousemove', handleMouseMove)
			window.removeEventListener('mouseup', handleMouseUp)
		}
	}, [isDragging, handleMouseMove, handleMouseUp])

	// Handle close
	const handleClose = () => {
		globalAudio.stop()
		dispatch(closePlayer())
	}

	// Handle collapse toggle
	const handleCollapseToggle = () => {
		dispatch(toggleCollapse())
	}

	// Handle cover image error
	const handleImageError = () => {
		setCoverSrc(defaultImage)
	}

	// If not open or no content, don't render
	if (!isOpen || !content) {
		return null
	}

	const progress = duration > 0 ? (currentTime / duration) * 100 : 0

	return (
		<GlobalPlayerContainer
			ref={containerRef}
			style={{
				transform: `translate(${playerPosition.x}px, ${playerPosition.y}px)`,
			}}
			$isCollapsed={isCollapsed}
		>
			<ControlsBar onMouseDown={handleMouseDown}>
				<DragHandle />
				<div className="flex items-center gap-2">
					<div
						className="flex-shrink-0 h-10 w-10 rounded flex items-center justify-center cursor-pointer hover:bg-accent transition-colors"
						onClick={handleCollapseToggle}
					>
						{isCollapsed ? (
							<ChevronUp className="h-5 w-5" />
						) : (
							<ChevronDown className="h-5 w-5" />
						)}
					</div>
					<div
						className="flex-shrink-0 h-10 w-10 rounded flex items-center justify-center cursor-pointer hover:bg-accent transition-colors"
						onClick={handleClose}
					>
						<X className="h-5 w-5" />
					</div>
				</div>
			</ControlsBar>

			<PlayerContainer $isCollapsed={isCollapsed}>
				<div className="flex items-center gap-4 p-4">
					{/* Cover Image */}
					{!content.hideCover && (
						<div className="w-[62px] h-[62px] shrink-0 rounded-lg overflow-hidden">
							<img
								src={coverSrc}
								alt={content.title}
								onError={handleImageError}
								className="w-full h-full object-cover"
							/>
						</div>
					)}

					{/* Track Info */}
					<div className="flex flex-col min-w-0 flex-shrink-0 max-w-[150px]">
						<div className="font-bold text-sm truncate">{content.title}</div>
						{content.metadata && content.metadata.length > 0 && (
							<div className="text-xs text-muted-foreground truncate">
								{content.metadata[0]}
							</div>
						)}
					</div>

					{/* Play/Pause Button */}
					<button
						onClick={handlePlayPause}
						aria-label={isPlaying ? 'Pause' : 'Play'}
						className="flex-shrink-0 h-10 w-10 rounded-full bg-foreground text-background flex items-center justify-center hover:opacity-80 transition-opacity"
					>
						{isPlaying ? (
							<Pause className="h-5 w-5" />
						) : (
							<Play className="h-5 w-5 ml-0.5" />
						)}
					</button>

					{/* Progress Section */}
					<div className="flex items-center gap-2 flex-1 min-w-0">
						<span className="text-xs text-muted-foreground w-10 text-right flex-shrink-0">
							{formatTime(currentTime)}
						</span>
						<div
							className="flex-1 h-1 bg-muted rounded-full cursor-pointer relative min-w-[100px]"
							onClick={handleSeek}
						>
							<div
								className="h-full bg-foreground rounded-full"
								style={{ width: `${progress}%` }}
							/>
						</div>
						<span className="text-xs text-muted-foreground w-10 flex-shrink-0">
							{formatTime(duration)}
						</span>
					</div>

					{/* Volume Controls */}
					<div className="flex items-center gap-2 flex-shrink-0">
						<button
							onClick={handleMuteToggle}
							className="h-8 w-8 flex items-center justify-center hover:bg-accent rounded transition-colors"
						>
							{isMuted || volume === 0 ? (
								<VolumeX className="h-4 w-4" />
							) : (
								<Volume2 className="h-4 w-4" />
							)}
						</button>
						<div
							className="w-20 h-1 bg-muted rounded-full cursor-pointer relative"
							onClick={handleVolumeChange}
						>
							<div
								className="h-full bg-foreground rounded-full"
								style={{ width: `${isMuted ? 0 : volume * 100}%` }}
							/>
						</div>
					</div>
				</div>
			</PlayerContainer>
		</GlobalPlayerContainer>
	)
}

// Styled components
const GlobalPlayerContainer = styled.div.attrs({
	className: 'drawer-blur md:bg-card md:border',
})<{ $isCollapsed: boolean }>`
	position: fixed;
	bottom: 20px;
	left: 50%;
	margin-left: ${(props) => (props.$isCollapsed ? '-90px' : '-300px')};
	z-index: 1050;
	width: ${(props) => (props.$isCollapsed ? '180px' : '750px')};
	max-width: 90vw;
	border: 1px solid var(--border) !important;
	border-radius: 8px;
	box-shadow:
		0 0 0 1px var(--border),
		0 4px 24px rgba(0, 0, 0, 0.2);
	overflow: hidden;
	cursor: grab;
	transition: width 0.3s ease;

	&:active {
		cursor: grabbing;
	}

	@media (max-width: 768px) {
		width: ${(props) => (props.$isCollapsed ? '180px' : '90vw')};
		margin-left: ${(props) => (props.$isCollapsed ? '-90px' : '-45vw')};
	}
`

const ControlsBar = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 0 12px;
	background-color: transparent;
`

const DragHandle = styled.div`
	width: 50px;
	height: 4px;
	background-color: var(--muted);
	border-radius: 2px;
	margin: 0 auto;
`

const PlayerContainer = styled.div<{ $isCollapsed: boolean }>`
	height: ${(props) => (props.$isCollapsed ? '0' : 'auto')};
	max-height: ${(props) => (props.$isCollapsed ? '0' : '1000px')};
	overflow: hidden;
	opacity: ${(props) => (props.$isCollapsed ? '0' : '1')};
	transition: all 0.3s ease;
	pointer-events: ${(props) => (props.$isCollapsed ? 'none' : 'auto')};

	/* Ensure text doesn't wrap */
	.truncate {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
`

export default GlobalPlayer
